import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isPremium } from "@/components/PremiumGate";

/**
 * Growth Patterns — the dedicated home for /insights/patterns.
 *
 * Surfaces four lenses on top of the same 90-day pattern read:
 *   Recurring Struggles · Answered Prayers · Biblical Themes · Stewardship Opportunities
 *
 * The AI runs once per visit; results are held in local component state. The visual
 * language is the same editorial vine as the rest of Cultiv.
 */

const Section = ({ eyebrow, heading, subheading, children, testid }) => (
    <section className="hairline rounded-lg p-8 md:p-10" data-testid={testid}>
        <div className="eyebrow-gold mb-3">{eyebrow}</div>
        <h2 className="font-serif text-2xl md:text-3xl leading-tight">{heading}</h2>
        {subheading && <p className="mt-2 text-sm text-muted-foreground max-w-2xl leading-relaxed">{subheading}</p>}
        <div className="mt-8">{children}</div>
    </section>
);

export default function GrowthPatterns() {
    const { user } = useAuth();
    const premium = isPremium(user);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/insights/patterns");
                setData(data);
            } catch {
                setData({});
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    if (loading) return <div className="text-muted-foreground py-16">Considering the last ninety days…</div>;

    const hasAnything =
        (data?.patterns?.length || 0) +
        (data?.recurring_struggles?.length || 0) +
        (data?.biblical_themes?.length || 0) +
        (data?.stewardship_opportunities?.length || 0) > 0;

    return (
        <div className="space-y-16">
            <motion.header
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="max-w-3xl"
            >
                <div className="eyebrow-gold mb-4">Journal Intelligence</div>
                <h1 className="font-serif text-5xl md:text-7xl leading-[1.02]">What has been quietly true.</h1>
                <p className="mt-6 text-muted-foreground text-lg leading-relaxed">
                    Cultiv reads the last ninety days of your journal, prayers, and Blueprint alongside your assessment,
                    and names the through-lines you may not see. The goal is not to inform you — it is to grow your
                    discernment, so you can begin to recognize these patterns yourself.
                </p>
            </motion.header>

            {!hasAnything && (
                <section className="hairline rounded-lg p-10 md:p-14">
                    <div className="eyebrow mb-3">Not enough soil yet</div>
                    <p className="font-serif text-2xl md:text-3xl leading-snug">
                        Once you have a few journal entries or prayers on the page, this space will begin to name the
                        patterns underneath.
                    </p>
                    <div className="mt-8 flex flex-wrap gap-3">
                        <Link to="/journal" className="eyebrow hover:text-foreground transition-colors inline-flex items-center gap-2">
                            Open journal <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                        </Link>
                        <Link to="/prayer" className="eyebrow hover:text-foreground transition-colors inline-flex items-center gap-2">
                            Open prayer &amp; rhythms <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                        </Link>
                    </div>
                </section>
            )}

            {/* Through-line summary */}
            {data?.summary && (
                <section className="max-w-3xl">
                    <div className="eyebrow mb-3">The through-line</div>
                    <p className="prose-cultiv font-serif text-2xl md:text-3xl leading-[1.35]" data-testid="growth-summary">
                        {data.summary}
                    </p>
                </section>
            )}

            {/* Patterns (existing) */}
            {data?.patterns?.length > 0 && (
                <Section
                    eyebrow="What is showing up"
                    heading="Patterns across the garden entrusted to you."
                    subheading="Each observation names a relationship between two or more of your eight areas — because they are one life."
                    testid="patterns-section"
                >
                    <ol className="space-y-6 list-none pl-0">
                        {data.patterns.map((p, i) => (
                            <li key={i} className="flex gap-6 items-baseline">
                                <span className="font-serif text-3xl text-[hsl(var(--eden-gold))]/70">{String(i + 1).padStart(2, "0")}</span>
                                <span className="prose-cultiv">{p}</span>
                            </li>
                        ))}
                    </ol>
                </Section>
            )}

            {/* Recurring Struggles */}
            {data?.recurring_struggles?.length > 0 && (
                <Section
                    eyebrow="Recurring struggles"
                    heading="What quietly returns."
                    subheading="Not to shame — to be seen. Scripture speaks to each of these; the next honest step is small."
                    testid="recurring-struggles-section"
                >
                    <div className="space-y-8">
                        {data.recurring_struggles.map((s, i) => (
                            <div key={i} className="border-l border-foreground/10 pl-6">
                                <div className="font-serif text-xl leading-snug mb-3">{s.struggle}</div>
                                {s.biblical_principle && (
                                    <p className="text-sm text-muted-foreground leading-relaxed italic mb-2">
                                        {s.biblical_principle}
                                    </p>
                                )}
                                {s.stewardship_response && (
                                    <p className="text-sm leading-relaxed">
                                        <span className="eyebrow-gold mr-2">This week</span> {s.stewardship_response}
                                    </p>
                                )}
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* Answered Prayers */}
            {data?.answered_prayers?.length > 0 && (
                <Section
                    eyebrow="Quietly answered"
                    heading="Prayers that have been tended."
                    subheading="Cultiv looks back to help you remember God's faithfulness — never to keep score."
                    testid="answered-prayers-section"
                >
                    <ul className="space-y-4 list-none pl-0">
                        {data.answered_prayers.map((a, i) => (
                            <li key={i} className="flex gap-4 items-baseline">
                                <Sparkles className="w-3 h-3 text-[hsl(var(--eden-gold))] mt-2 flex-shrink-0" strokeWidth={1.5} />
                                <span className="text-base leading-relaxed">{a}</span>
                            </li>
                        ))}
                    </ul>
                </Section>
            )}

            {/* Biblical Themes */}
            {data?.biblical_themes?.length > 0 && (
                <Section
                    eyebrow="Biblical themes"
                    heading="What Scripture is teaching you right now."
                    subheading="These are the principles rising through your own words. Sit with the passages, not just the theme."
                    testid="biblical-themes-section"
                >
                    <div className="grid md:grid-cols-2 gap-6">
                        {data.biblical_themes.map((t, i) => (
                            <div key={i} className="border border-foreground/10 rounded-md p-6">
                                <div className="font-serif text-xl leading-tight mb-1">{t.theme}</div>
                                {t.passage && <div className="eyebrow-gold mb-3">{t.passage}</div>}
                                {t.why_here && (
                                    <p className="text-sm text-muted-foreground leading-relaxed">{t.why_here}</p>
                                )}
                            </div>
                        ))}
                    </div>
                </Section>
            )}

            {/* Stewardship Opportunities */}
            {data?.stewardship_opportunities?.length > 0 && (
                <Section
                    eyebrow="Faithful next steps"
                    heading="One honest week of tending."
                    subheading="Small enough to actually do. Rooted in what you have already written."
                    testid="opportunities-section"
                >
                    <ol className="space-y-5 list-none pl-0">
                        {data.stewardship_opportunities.map((o, i) => (
                            <li key={i} className="flex gap-5 items-baseline">
                                <span className="font-serif text-2xl text-[hsl(var(--eden-gold))]/70 leading-none">·</span>
                                <span className="text-base leading-relaxed">{o}</span>
                            </li>
                        ))}
                    </ol>
                </Section>
            )}

            {!premium && (
                <section className="hairline rounded-lg p-8 max-w-3xl">
                    <div className="eyebrow-gold mb-2">Premium · Ongoing Insights</div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                        Premium keeps this pattern-reading running as your journals and prayers grow — naming the
                        through-lines that only appear over months, and helping your discernment grow with them.{" "}
                        <Link to="/membership" className="text-[hsl(var(--eden-gold))] hover:underline">Continue cultivating &rarr;</Link>
                    </p>
                </section>
            )}
        </div>
    );
}
